
import { ReactNode, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { isLovableEditor } from "@/utils/environment";
import { verifySession } from "@/integrations/supabase/client";

interface AuthGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  adminOnly?: boolean;
  requireVendor?: boolean;
  fallbackUrl?: string;
}

export const AuthGuard = ({
  children,
  requireAuth = true,
  adminOnly = false,
  requireVendor = false,
  fallbackUrl = "/auth"
}: AuthGuardProps) => {
  const { isAuthenticated, isLoading, isAdmin, isVendor } = useAuth();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [hasVerifiedSession, setHasVerifiedSession] = useState(false);

  useEffect(() => {
    let redirectTimer: NodeJS.Timeout | null = null;
    let mounted = true;
    
    const checkAuthorization = async () => {
      console.log(`[AuthGuard] Checking authorization for ${location.pathname}`);
      
      // Handle public routes that don't require authentication
      const isPublicRoute = 
        location.pathname === "/auth" || 
        location.pathname === "/" || 
        location.pathname.startsWith("/service/") ||
        location.pathname.includes("/terms-of-service");
      
      if (isPublicRoute && !requireAuth && !adminOnly && !requireVendor) {
        console.log(`[AuthGuard] Public route access granted: ${location.pathname}`);
        if (mounted) {
          setIsAuthorized(true);
          setIsChecking(false);
        }
        return;
      }
      
      // Wait for auth state to be loaded
      if (isLoading) {
        console.log(`[AuthGuard] Still loading auth state for ${location.pathname}`);
        if (mounted) {
          setIsChecking(true);
        }
        return;
      }
      
      // Extra session verification for Dev mode issues
      const isDev = process.env.NODE_ENV !== 'production' || isLovableEditor();
      
      if (isDev && requireAuth && !hasVerifiedSession) {
        console.log(`[AuthGuard] Dev mode: Performing additional session verification`);
        try {
          const sessionValid = await verifySession();
          console.log(`[AuthGuard] Dev mode session verification:`, sessionValid ? "Valid" : "Invalid");
          if (mounted) {
            setHasVerifiedSession(true);
          }
          
          // If we think we're authenticated but session is invalid,
          // we need to invalidate our state and force reload auth
          if (isAuthenticated && !sessionValid && mounted) {
            console.log(`[AuthGuard] Dev mode: Session validation mismatch detected`);
            // This is likely a broken auth state situation
            // This reload will force a clean state
            window.location.href = fallbackUrl;
            return;
          }
        } catch (error) {
          console.error("[AuthGuard] Error verifying session:", error);
        }
      }
      
      // Check authorization rules
      let authorized = true;
      
      if (requireAuth && !isAuthenticated) {
        console.log(`[AuthGuard] Auth required but user not authenticated for ${location.pathname}`);
        authorized = false;
      }
      
      if (adminOnly && (!isAuthenticated || !isAdmin)) {
        console.log(`[AuthGuard] Admin required but user not admin for ${location.pathname}`);
        authorized = false;
      }
      
      if (requireVendor && (!isAuthenticated || !isVendor)) {
        console.log(`[AuthGuard] Vendor required but user not vendor for ${location.pathname}`);
        authorized = false;
      }
      
      if (mounted) {
        setIsAuthorized(authorized);
        setIsChecking(false);
      }
      
      if (!authorized) {
        console.log(`[AuthGuard] Unauthorized access to ${location.pathname}, redirecting to ${fallbackUrl}`);
        
        if (redirectTimer) {
          clearTimeout(redirectTimer);
        }
        
        const isDev = process.env.NODE_ENV !== 'production' || isLovableEditor();
        // In dev mode, add a small delay to allow for editor detection to stabilize
        const redirectDelay = isDev ? 150 : 100;
        
        // Using requestAnimationFrame to ensure redirect happens after render
        redirectTimer = setTimeout(() => {
          if (mounted) {
            // Preserve the attempted URL in state for potential redirection after login
            navigate(fallbackUrl, { state: { from: location.pathname } });
          }
        }, redirectDelay);
      }
    };
    
    checkAuthorization();
    
    return () => {
      mounted = false;
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [isAuthenticated, isLoading, isAdmin, isVendor, location.pathname, navigate, fallbackUrl, requireAuth, adminOnly, requireVendor, hasVerifiedSession]);

  if (isChecking) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Authorizing...</span>
      </div>
    );
  }

  return isAuthorized ? <>{children}</> : null;
};
